'use client';

import { Badge, Box, Flex, HStack, Separator, Text, VStack } from '@chakra-ui/react';
import { createContext, useContext, type ReactNode } from 'react';

type IntegrationTone = 'connected' | 'warning' | 'idle' | 'danger' | 'neutral';
type IntegrationSurfaceMode = 'card' | 'flat';

const IntegrationSurfaceModeContext = createContext<IntegrationSurfaceMode>('card');

export function IntegrationSurfaceProvider({
  mode,
  children,
}: {
  mode: IntegrationSurfaceMode;
  children: ReactNode;
}) {
  return (
    <IntegrationSurfaceModeContext.Provider value={mode}>
      {children}
    </IntegrationSurfaceModeContext.Provider>
  );
}

export function useIntegrationSurfaceMode() {
  return useContext(IntegrationSurfaceModeContext);
}

const BADGE_FOREGROUNDS: Record<IntegrationTone, string> = {
  connected: 'status.success.fg',
  warning: 'status.warning.fg',
  idle: 'ui.text.secondary',
  danger: 'status.danger.fg',
  neutral: 'ui.text.secondary',
};

const ICON_BACKGROUNDS: Record<IntegrationTone, string> = {
  connected: 'status.success.bg',
  warning: 'status.warning.bg',
  idle: 'ui.surface.selected',
  danger: 'status.danger.bg',
  neutral: 'ui.surface.selected',
};

export function IntegrationCard({
  icon,
  title,
  description,
  statusLabel,
  statusTone = 'neutral',
  action,
  meta,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  statusLabel?: string;
  statusTone?: IntegrationTone;
  action?: ReactNode;
  meta?: ReactNode;
  children?: ReactNode;
}) {
  const surfaceMode = useIntegrationSurfaceMode();

  if (surfaceMode === 'flat') {
    return (
      <Box h="full">
        <VStack align="stretch" gap="3" h="full">
          {(statusLabel || action || meta) && (
            <Flex
              justify="space-between"
              align={{ base: 'stretch', sm: 'center' }}
              direction={{ base: 'column', sm: 'row' }}
              gap="3"
              pb={children ? '3' : '0'}
              borderBottomWidth={children ? '1px' : '0'}
              borderBottomStyle="solid"
              borderBottomColor="ui.border.subtle"
            >
              <HStack gap="2" flexWrap="wrap">
                {statusLabel && (
                  <Badge
                    bg={ICON_BACKGROUNDS[statusTone]}
                    color={BADGE_FOREGROUNDS[statusTone]}
                    variant="subtle"
                    size="sm"
                  >
                    {statusLabel}
                  </Badge>
                )}
                {meta}
              </HStack>
              {action && <Box flexShrink={0}>{action}</Box>}
            </Flex>
          )}

          {children && (
            <VStack align="stretch" gap="0" flex="1">
              {children}
            </VStack>
          )}
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      layerStyle="surfaceCard"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="ui.border.default"
      borderRadius="glassCard"
      boxShadow="none"
      p={{ base: '4', md: '5' }}
      h="full"
    >
      <VStack align="stretch" gap="4" h="full">
        <Flex
          justify="space-between"
          align={{ base: 'stretch', sm: 'flex-start' }}
          direction={{ base: 'column', sm: 'row' }}
          gap="4"
        >
          <HStack align="flex-start" gap="3" minW={0}>
            <Box
              boxSize="9"
              borderRadius="glassButton"
              bg={ICON_BACKGROUNDS[statusTone]}
              color="ui.text.primary"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              {icon}
            </Box>
            <Box minW={0}>
              <HStack gap="2" mb="1" flexWrap="wrap">
                <Text fontWeight="650" color="ui.text.primary">
                  {title}
                </Text>
                {statusLabel && (
                  <Badge
                    bg={ICON_BACKGROUNDS[statusTone]}
                    color={BADGE_FOREGROUNDS[statusTone]}
                    variant="subtle"
                    size="sm"
                  >
                    {statusLabel}
                  </Badge>
                )}
              </HStack>
              <Text fontSize="sm" color="ui.text.secondary">
                {description}
              </Text>
              {meta && (
                <Box mt="2" color="ui.text.tertiary" fontSize="xs">
                  {meta}
                </Box>
              )}
            </Box>
          </HStack>
          {action && <Box flexShrink={0}>{action}</Box>}
        </Flex>

        {children && (
          <>
            <Separator borderColor="ui.border.subtle" />
            <VStack align="stretch" gap="0" flex="1">
              {children}
            </VStack>
          </>
        )}
      </VStack>
    </Box>
  );
}

export function IntegrationStatusRow({
  icon,
  title,
  description,
  action,
  children,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Flex
      justify="space-between"
      align={{ base: 'stretch', md: 'center' }}
      direction={{ base: 'column', md: 'row' }}
      gap="3"
      py="3"
      minH="56px"
    >
      <HStack align="flex-start" gap="3" minW={0}>
        {icon && (
          <Box
            boxSize="8"
            borderRadius="glassButton"
            bg="ui.surface.selected"
            color="ui.text.secondary"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            {icon}
          </Box>
        )}
        <Box minW={0}>
          <Text fontWeight="600" color="ui.text.primary" fontSize="sm">
            {title}
          </Text>
          {description && (
            <Box fontSize="xs" color="ui.text.tertiary" mt="0.5">
              {description}
            </Box>
          )}
          {children}
        </Box>
      </HStack>
      {action && <Box flexShrink={0}>{action}</Box>}
    </Flex>
  );
}

export function IntegrationNotice({
  icon,
  children,
  tone = 'warning',
  action,
}: {
  icon?: ReactNode;
  children: ReactNode;
  tone?: 'warning' | 'danger' | 'neutral';
  action?: ReactNode;
}) {
  const colors = {
    warning: {
      bg: 'status.warning.bg',
      border: 'status.warning.border',
      text: 'status.warning.fg',
    },
    danger: {
      bg: 'status.danger.bg',
      border: 'status.danger.border',
      text: 'status.danger.fg',
    },
    neutral: {
      bg: 'ui.surface.selected',
      border: 'ui.border.subtle',
      text: 'ui.text.secondary',
    },
  }[tone];

  return (
    <Flex
      align={{ base: 'stretch', md: 'center' }}
      direction={{ base: 'column', md: 'row' }}
      justify="space-between"
      gap="3"
      p="3"
      bg={colors.bg}
      borderWidth="1px"
      borderStyle="solid"
      borderColor={colors.border}
      borderRadius="glass"
      mt="2"
    >
      <HStack align="flex-start" gap="2">
        {icon}
        <Text fontSize="sm" color={colors.text}>
          {children}
        </Text>
      </HStack>
      {action && <Box flexShrink={0}>{action}</Box>}
    </Flex>
  );
}
